import jwt_decode from 'jwt-decode';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import client, { AccessToken } from 'services/client';
import { isChildSortingCenter, isParentSortingCenter } from 'services/SortingCenterHierarchy';
import { SortingCenter, SortingCenterUserRole, User, UserRole } from 'types';

interface Props {
  children: any;
}

const SORTING_CENTER_LOCALSTORAGE_KEY = 'sortingCenter';

export interface Context {
  user?: User | null | false;
  setUser?: any;
  getRoles?: any;
  setActiveSortingCenter?: (sortingCenter: SortingCenter) => void;
  getSortingCenter?: () => SortingCenter | null;
  getSortingCenters?: () => SortingCenter[] | null;
  resetPassword?: any;
  changePassword?: any;
  logout?: any;
  login?: any;
  hasLeastOneAdminRole?: any;
  isEdgeBrowser?: () => boolean;
}

export const AppContext = createContext<Context>({});

export const AppProvider: React.FC<Props> = ({ children }) => {
  const [user, setUser] = useState<User | null | false>(null);
  const [activeSortingCenter, setActiveSortingCenter] = useState<SortingCenter | null>(null);

  const refreshUser = useCallback(async () => {
    if (client.getToken()) {
      const refreshedUser = (await client.get('/auth/me')) as User;
      const requestedSortingCenterId = localStorage.getItem(SORTING_CENTER_LOCALSTORAGE_KEY);

      if (
        (!activeSortingCenter && requestedSortingCenterId) ||
        (activeSortingCenter && requestedSortingCenterId !== activeSortingCenter['@id'])
      ) {
        const requestedSortingCenterUserRole = refreshedUser.sortingCenterUserRoles.find(
          ({ sortingCenter }) => sortingCenter['@id'] === requestedSortingCenterId,
        );
        if (requestedSortingCenterUserRole) {
          setActiveSortingCenter(requestedSortingCenterUserRole.sortingCenter);
        } else {
          let childrenSortingCenterFound = null;
          for (const { sortingCenter } of refreshedUser.sortingCenterUserRoles) {
            if (sortingCenter.childrenSortingCenter) {
              childrenSortingCenterFound = sortingCenter.childrenSortingCenter.find(
                sortingCenter => sortingCenter['@id'] === requestedSortingCenterId,
              );

              if (childrenSortingCenterFound) {
                childrenSortingCenterFound.parentSortingCenter = sortingCenter;
                break;
              }
            }
          }

          if (childrenSortingCenterFound) {
            setActiveSortingCenter(childrenSortingCenterFound);
          } else {
            localStorage.removeItem(SORTING_CENTER_LOCALSTORAGE_KEY);
          }
        }
      }

      if (!activeSortingCenter && refreshedUser.sortingCenterUserRoles.length === 1) {
        const currentSortingCenter = refreshedUser.sortingCenterUserRoles[0].sortingCenter;

        // The user is not an admin of the sorting center AND has only one instance
        if (!isParentSortingCenter(currentSortingCenter)) {
          setActiveSortingCenter(currentSortingCenter);
        }
      }

      setUser(refreshedUser);
    } else {
      setUser(false);
      setActiveSortingCenter(null);
      localStorage.removeItem(SORTING_CENTER_LOCALSTORAGE_KEY);
    }
  }, [activeSortingCenter]);

  const resetPassword = async (data: { email: string }) => {
    await client.post('/forgot_password/', data, { checkToken: false });
  };

  const changePassword = async (token: string, data: { password: string }) => {
    await client.post('/forgot_password/' + token, data, { checkToken: false });
  };

  const logout = async () => {
    await client.logout();

    client.updateToken('');
    await refreshUser();
  };

  const login = async (data: { username: string; password: string }) => {
    await client.login(data);
    await refreshUser();
  };

  const getRoles = (): UserRole[] | null => {
    const jwtToken = client.getToken();
    if (!jwtToken) return null;

    const accessToken = jwt_decode<AccessToken>(jwtToken);

    const sortingCenterRole = getActiveSortingCenterRole()?.role;

    return sortingCenterRole ? [...accessToken.roles, sortingCenterRole] : accessToken.roles;
  };

  const getActiveSortingCenterRole = (): SortingCenterUserRole | null => {
    if (!user || !activeSortingCenter) {
      return null;
    }
    const sortingCenterUserRoleAdminOfParent =
      isChildSortingCenter(activeSortingCenter) &&
      user?.sortingCenterUserRoles.find(({ sortingCenter, role }) => {
        return (
          activeSortingCenter.parentSortingCenter?.['@id'] === sortingCenter['@id'] &&
          (UserRole.ROLE_ADMIN === role || UserRole.ROLE_SUPPORT === role)
        );
      });

    return sortingCenterUserRoleAdminOfParent
      ? sortingCenterUserRoleAdminOfParent
      : user?.sortingCenterUserRoles.find(({ sortingCenter }) => sortingCenter['@id'] === activeSortingCenter['@id']) ??
          null;
  };

  const getSortingCenter = (): SortingCenter | null => activeSortingCenter;

  const getSortingCenters = (): SortingCenter[] | null => {
    if (!user) {
      return null;
    }

    return user?.sortingCenterUserRoles?.map(({ sortingCenter }) => sortingCenter) ?? null;
  };

  const hasLeastOneAdminRole = (): boolean | null => {
    if (!user) {
      return null;
    }

    return Boolean(
      user.sortingCenterUserRoles?.find(
        sortingCenterUserRole =>
          sortingCenterUserRole['role'] === UserRole.ROLE_ADMIN ||
          sortingCenterUserRole['role'] === UserRole.ROLE_SUPPORT,
      ),
    );
  };

  const isEdgeBrowser = () => {
    return /msie\s|trident\/|edg\//i.test(window.navigator.userAgent);
  };

  useEffect(() => {
    client.checkToken().then(refreshUser);
    // This effect should only trigger on initial page load.
    // Adding refreshUser to the deps means we will refresh the user when changing
    // the active sorting center, and may provoke loops when disconnecting and resetting
    // both user and the active sorting center to
    // see ET-2546 for more informations
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSortingCenter) {
      localStorage.setItem(SORTING_CENTER_LOCALSTORAGE_KEY, activeSortingCenter['@id']);
    }
  }, [activeSortingCenter]);

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        getRoles,
        getSortingCenter,
        setActiveSortingCenter,
        getSortingCenters,
        resetPassword,
        changePassword,
        logout,
        login,
        hasLeastOneAdminRole,
        isEdgeBrowser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
